'use client'

import { Button } from '@/ui/atoms/button/Button'
import { Form, FormControl, FormField, FormItem } from '@/ui/atoms/form/Form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/atoms/select/Select'
import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { zodResolver } from '@hookform/resolvers/zod'
import { Check, Loader2, X } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { LIMIT_FREQUENCY_OPTIONS, type RainFrequency } from '../hooks/useBorrowLimit'
import {
  type CardLimitTier,
  formatUSD,
  getLimitCapError,
  getSelectedPeriodMaxUSD,
  getTierLimitSummary,
  getTierLimitTooltip,
} from '../utils/limitCaps'

const spendLimitSchema = z.object({
  limit: z
    .string()
    .min(1, 'Required')
    .refine((val) => !Number.isNaN(Number(val)) && Number(val) >= 1, 'Must be at least $1'),
  frequency: z.string().min(1),
})

type SpendLimitFormValues = z.infer<typeof spendLimitSchema>

function SpendRing({ percent, size = 80 }: { percent: number; size?: number }) {
  const strokeWidth = 8
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (percent / 100) * circumference

  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <defs>
        <linearGradient id="spendRingGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FBE572" />
          <stop offset="100%" stopColor="#4ADE80" />
        </linearGradient>
      </defs>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-muted/30"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="url(#spendRingGradient)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-all duration-500"
      />
    </svg>
  )
}

interface SpendLimitInlineProps {
  spendLimit?: number
  frequency: RainFrequency
  totalSpent: number
  maxCapacity: number
  tier?: CardLimitTier
  onUpdateLimit: (limit: number, frequency?: RainFrequency) => Promise<void>
  isUpdating: boolean
}

export function SpendLimitInline({
  spendLimit,
  frequency,
  totalSpent,
  maxCapacity,
  tier,
  onUpdateLimit,
  isUpdating,
}: SpendLimitInlineProps) {
  const [isEditing, setIsEditing] = useState(false)

  const hasLimit = spendLimit !== undefined && spendLimit > 0
  const percentUsed = hasLimit ? Math.min((totalSpent / spendLimit) * 100, 100) : 0
  const remaining = hasLimit ? Math.max(spendLimit - totalSpent, 0) : maxCapacity

  const isNearLimit = percentUsed >= 75
  const isAtLimit = percentUsed >= 95
  const form = useForm<SpendLimitFormValues>({
    resolver: zodResolver(spendLimitSchema),
    mode: 'onChange',
    defaultValues: {
      limit: hasLimit ? spendLimit.toString() : '',
      frequency,
    },
  })
  const selectedFrequency = (form.watch('frequency') as RainFrequency) || frequency
  const frequencyLabel = LIMIT_FREQUENCY_OPTIONS.find((o) => o.value === selectedFrequency)?.label ?? selectedFrequency
  const selectedPeriodMax = getSelectedPeriodMaxUSD({
    tier,
    frequency: selectedFrequency,
  })
  const limitCapError = getLimitCapError(tier, selectedFrequency, selectedPeriodMax)
  const tierLimitSummary = getTierLimitSummary(tier)
  const tierLimitTooltip = getTierLimitTooltip(tier, selectedFrequency, selectedPeriodMax)

  const handleStartEdit = () => {
    const startingPeriodMax = getSelectedPeriodMaxUSD({
      tier,
      frequency,
    })
    form.reset({
      limit: hasLimit ? spendLimit.toString() : '',
      frequency,
    })
    if (hasLimit && spendLimit > startingPeriodMax) {
      form.setError('limit', { message: getLimitCapError(tier, frequency, startingPeriodMax) })
    }
    setIsEditing(true)
  }

  const handleSave = async (values: SpendLimitFormValues) => {
    const nextLimit = Number(values.limit)
    if (nextLimit > selectedPeriodMax) {
      form.setError('limit', { message: limitCapError })
      return
    }
    await onUpdateLimit(nextLimit, values.frequency as RainFrequency)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setIsEditing(false)
    form.reset()
  }

  return (
    <div className="flex items-center gap-4">
      <div className="relative flex shrink-0 items-center justify-center">
        <SpendRing percent={hasLimit ? percentUsed : 0} size={72} />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <Typography
            variant="p"
            className={cn(
              'font-bold text-sm',
              isAtLimit ? 'text-red-400' : isNearLimit ? 'text-amber-400' : 'text-foreground',
            )}
          >
            {hasLimit ? `${Math.round(percentUsed)}%` : '—'}
          </Typography>
        </div>
      </div>

      <div className="flex-1">
        <div className="mb-1 flex items-center gap-2">
          <Typography variant="p" className="font-medium">
            {frequencyLabel} Limit
          </Typography>
          {tierLimitSummary && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="rounded-full border border-border/60 px-2 py-0.5 text-muted-foreground text-xs transition-colors hover:border-primary/40 hover:text-foreground"
                >
                  {tierLimitSummary} ⓘ
                </button>
              </TooltipTrigger>
              <TooltipContentShort>{tierLimitTooltip}</TooltipContentShort>
            </Tooltip>
          )}
        </div>

        {isEditing ? (
          <Form {...form}>
            <form noValidate onSubmit={form.handleSubmit(handleSave)} className="space-y-1">
              <div className="flex items-center gap-2">
                <FormField
                  control={form.control}
                  name="limit"
                  render={({ field }) => (
                    <FormItem className="flex-1 space-y-0">
                      <FormControl>
                        <div
                          className={cn(
                            'flex h-9 items-center gap-1.5 rounded-sm border border-input bg-input px-3 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                            form.formState.errors.limit && 'border-red-500/50',
                          )}
                        >
                          <span className="text-muted-foreground text-sm">$</span>
                          <input
                            {...field}
                            onChange={(e) => {
                              const raw = e.target.value
                              if (raw === '') {
                                field.onChange('')
                                return
                              }
                              const num = Number(raw)
                              if (num < 0) return
                              field.onChange(raw)
                              if (Number.isNaN(num)) {
                                return
                              }
                              if (num > selectedPeriodMax) {
                                form.setError('limit', { message: limitCapError })
                              } else {
                                form.clearErrors('limit')
                              }
                            }}
                            type="number"
                            step="1"
                            min="1"
                            max={selectedPeriodMax}
                            placeholder="0"
                            className="min-w-0 flex-1 bg-transparent text-foreground text-sm focus:outline-none"
                            autoFocus
                            disabled={isUpdating}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              field.onChange(String(selectedPeriodMax))
                              form.clearErrors('limit')
                            }}
                            disabled={isUpdating}
                            className="shrink-0 rounded px-1 py-0.5 text-primary text-xs hover:bg-primary/10"
                          >
                            MAX
                          </button>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="frequency"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value)
                        const nextFrequency = value as RainFrequency
                        const nextMax = getSelectedPeriodMaxUSD({
                          tier,
                          frequency: nextFrequency,
                        })
                        const currentLimit = Number(form.getValues('limit'))

                        if (currentLimit > nextMax) {
                          form.setError('limit', {
                            message: getLimitCapError(tier, nextFrequency, nextMax),
                          })
                        } else {
                          form.clearErrors('limit')
                        }
                      }}
                      disabled={isUpdating}
                    >
                      <SelectTrigger className="h-9 w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LIMIT_FREQUENCY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {isUpdating ? (
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
                ) : (
                  <>
                    <button
                      type="submit"
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-500/20 text-green-400 transition-colors hover:bg-green-500/30"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-500/20 text-red-400 transition-colors hover:bg-red-500/30"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
              {form.formState.errors.limit && (
                <p className="text-red-400 text-xs">{form.formState.errors.limit.message}</p>
              )}
            </form>
          </Form>
        ) : (
          <div className="flex flex-col gap-0">
            {hasLimit ? (
              <>
                <Typography
                  variant="h4"
                  className={cn(
                    'font-bold',
                    isAtLimit
                      ? 'text-red-400'
                      : isNearLimit
                        ? 'text-amber-400'
                        : 'bg-gradient-to-r from-gradient-yellow to-gradient-green bg-clip-text text-transparent',
                  )}
                >
                  ${remaining.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
                <Typography variant="p" className="text-muted-foreground text-xs">
                  of ${spendLimit.toLocaleString(undefined, { maximumFractionDigits: 0 })}{' '}
                  {frequencyLabel.toLowerCase()}
                </Typography>
              </>
            ) : (
              <Typography variant="p" className="text-muted-foreground text-sm">
                No limit • Max ${formatUSD(selectedPeriodMax)}
              </Typography>
            )}
          </div>
        )}
      </div>

      {!isEditing && (
        <Button variant="ghost" size="sm" onClick={handleStartEdit} className="text-muted-foreground text-xs">
          {hasLimit ? 'Edit' : 'Set'}
        </Button>
      )}
    </div>
  )
}
